import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message, Spin } from "antd";
import "./styles.css";
import ContractHeader from "./components/Navigation/ContractHeader";
import ContractStepper from "./components/Navigation/ContractStepper";
import {
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
  Step6,
  Step7,
  Step8,
  Step9,
  Step10,
} from "./components/Steps";
import useContractData from "./hooks/useContractData";
import { useUserContext } from "../../../Contexts/user";

const FORM_STEPS = [3, 5, 6, 7, 8, 9, 10];

const Contract = () => {
  const { id: contractId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  const {userData} = useUserContext();
  const isAdmin = userData?.role !== "vendor";

  const {
    contract,
    stepData,
    loading,
    stepLoading,
    saving,
    error,
    validationErrors,
    loadStepData,
    saveStep,
    submitContract,
    clearError,
  } = useContractData(contractId, { isAdmin });

  const editable = contract?.editable ?? false;

  // Ref to hold latest form data from current step (for save-on-next)
  const latestFormDataRef = useRef(null);

  // Load step data when step changes
  useEffect(() => {
    if (FORM_STEPS.includes(currentStep)) {
      loadStepData(currentStep);
    }
  }, [currentStep, loadStepData]);

  // Clear latest form data ref when step changes
  useEffect(() => {
    latestFormDataRef.current = null;
  }, [currentStep]);

  // Called by step components on every form change (for save-on-next tracking)
  const handleDataChange = useCallback((data) => {
    latestFormDataRef.current = data;
  }, []);

  // Called by step components for auto-save
  const handleSave = useCallback(
    async (data) => {
      if (!editable) return;
      latestFormDataRef.current = data;
      try {
        await saveStep(currentStep, data);
      } catch {
        // Auto-save failures are silent
      }
    },
    [currentStep, editable, saveStep]
  );

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "instant" });

  const handleStepClick = (stepId) => {
    // Save current step data before leaving (if any pending data)
    if (editable && latestFormDataRef.current && FORM_STEPS.includes(currentStep)) {
      saveStep(currentStep, latestFormDataRef.current).catch(() => {});
    }
    setCurrentStep(stepId);
    scrollToTop();
  };

  const handleNext = async () => {
    // Save current step data before moving
    if (editable && latestFormDataRef.current && FORM_STEPS.includes(currentStep)) {
      try {
        await saveStep(currentStep, latestFormDataRef.current);
      } catch {
        message.error("Failed to save data");
      }
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }

    if (currentStep < 10) {
      setCurrentStep(currentStep + 1);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    // Save current step data before going back
    if (editable && latestFormDataRef.current && FORM_STEPS.includes(currentStep)) {
      saveStep(currentStep, latestFormDataRef.current).catch(() => {});
    }

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      scrollToTop();
    }
  };

  const handleFinalSubmit = async () => {
    // Save current step data first
    if (editable && latestFormDataRef.current && FORM_STEPS.includes(currentStep)) {
      try {
        await saveStep(currentStep, latestFormDataRef.current);
      } catch {
        message.error("Failed to save current step");
        return;
      }
    }

    try {
      const result = await submitContract();
      if (result?.success) {
        navigate(`/contract/${contractId}/thank-you`, {
          state: {
            exhibitorName: contract?.exhibitorName,
            contractId,
          },
        });
      } else if (result?.errors) {
        // Find the first step with errors and navigate to it
        const errorSteps = Object.keys(result.errors).map(Number).sort((a, b) => a - b);
        if (errorSteps.length > 0) {
          message.error(`Validation errors found in ${errorSteps.length} step(s). Please review and fix.`);
          setCurrentStep(errorSteps[0]);
          scrollToTop();
        }
      }
    } catch {
      message.error("Failed to submit contract");
    }
  };

  const exhibitorData = contract
    ? {
        exhibitorNumber: contract.exhibitorNumber,
        exhibitorName: contract.exhibitorName,
        merchant: contract.merchant,
      }
    : null;

  // Get errors for current step
  const currentStepErrors = validationErrors[currentStep] || {};

  const renderStep = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "var(--neutral-500)" }}>
            Loading contract...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <p style={{ color: "var(--danger)", fontSize: 16, marginBottom: 12 }}>
            {error}
          </p>
          <button className="btn btn-primary" onClick={() => { clearError(); window.location.reload(); }}>
            Retry
          </button>
        </div>
      );
    }

    // Show loading for steps that need data
    if (stepLoading && FORM_STEPS.includes(currentStep) && !stepData[currentStep]) {
      return (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "var(--neutral-500)" }}>
            Loading step data...
          </p>
        </div>
      );
    }

    const stepProps = {
      exhibitorData,
      disabled: !editable,
      errors: currentStepErrors,
      onSave: handleSave,
      onDataChange: handleDataChange,
      isAdmin: userData?.role !== "vendor",
    };

    switch (currentStep) {
      case 1:
        return <Step1 />;
      case 2:
        return <Step2 />;
      case 3:
        return (
          <Step3
            {...stepProps}
            initialContract={stepData[3] || {}}
          />
        );
      case 4:
        return <Step4 />;
      case 5:
        return (
          <Step5
            {...stepProps}
            initialContract={stepData[5] || {}}
          />
        );
      case 6:
        return (
          <Step6
            {...stepProps}
            initialContract={stepData[6] || {}}
          />
        );
      case 7:
        return (
          <Step7
            {...stepProps}
            initialContract={stepData[7] || {}}
          />
        );
      case 8:
        return (
          <Step8
            {...stepProps}
            initialContract={stepData[8] || {}}
          />
        );
      case 9:
        return (
          <Step9
            {...stepProps}
            initialContract={stepData[9] || {}}
          />
        );
      case 10:
        return (
          <Step10
            {...stepProps}
            initialContract={stepData[10] || {}}
          />
        );
      default:
        return <Step1 />;
    }
  };

  return (
    <div className="contract-app">
      {userData?.role !== "vendor" ? null: (
      <ContractHeader />)}
      <ContractStepper
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
        validationErrors={validationErrors}
      />

      <main className="contract-main">
        {renderStep()}

        {/* Navigation Buttons */}
        {!loading && !error && (
          <div className="form-navigation">
            <button
              className="btn btn-secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Previous
            </button>

            <div className="nav-right">
              {saving && (
                <span style={{ fontSize: 13, color: "var(--neutral-500)" }}>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  Saving...
                </span>
              )}

              {currentStep < 10 ? (
                <button className="btn btn-primary" onClick={handleNext}>
                  Next
                </button>
              ) : editable ? (
                <button
                  className="btn btn-submit"
                  onClick={handleFinalSubmit}
                >
                  Submit Contract
                </button>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Contract;
